/* Copyright (c) 2026 Health Endeavors, LLC. All Rights Reserved */

import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';

import { RootState } from '../../../../app/store';
import pearlApi from '../../api/pearlApi';
import { snackNotifications } from '../../notification/SnackBarUtils';

export interface MedPearlOneFormDataType {
  helpful_in_areas: string[];
  helpful_score: number | null;
  how_can_improve: string;
}
export interface FeedbackState {
  formData: MedPearlOneFormDataType;
  submittingFeedback: boolean;
}
const initialState: FeedbackState = {
  formData: { helpful_in_areas: [], helpful_score: null, how_can_improve: '' },
  submittingFeedback: false,
};

export const submitMedPearlOneFeedbackThunk = createAsyncThunk<boolean, MedPearlOneFormDataType, { state: RootState }>(
  'submitMedPearlOneFeedbackThunk',
  async (data, thunkAPI) => {
    try {
      const { messages } = thunkAPI.getState().chat;
      await pearlApi.post(
        `/feedbacks/medPearlOne/${messages[messages.length - 1]?.pages?.[0]?.document_version_id || ''}`,
        {
          data,
        }
      );
      snackNotifications.success('Thank you for your valuable Feedback!');
      return true;
    } catch (err) {
      snackNotifications.error(`Failed to submit feedback: ${(err as Error).message}`);
      throw err;
    }
  }
);

export const medPearlOneFeedbackSlice = createSlice({
  name: 'medPearlOneFeedback',
  initialState,
  reducers: {
    setHelpfulArea: (state, action: PayloadAction<string[]>) => {
      state.formData.helpful_in_areas = action.payload;
    },
    setHelpfulScore: (state, action: PayloadAction<number | null>) => {
      state.formData.helpful_score = action.payload;
    },
    setHowCanImprove: (state, action: PayloadAction<string>) => {
      state.formData.how_can_improve = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(submitMedPearlOneFeedbackThunk.pending, (state) => {
        state.submittingFeedback = true;
      })
      .addCase(submitMedPearlOneFeedbackThunk.fulfilled, (state) => {
        state.formData = initialState.formData;
        state.submittingFeedback = false;
      })
      .addCase(submitMedPearlOneFeedbackThunk.rejected, (state) => {
        state.submittingFeedback = false;
      });
  },
});
export const { setHelpfulArea, setHelpfulScore, setHowCanImprove } = medPearlOneFeedbackSlice.actions;
export const selectFeedbackInfo = (state: RootState): MedPearlOneFormDataType => state.medPearlOneFeedback.formData;
export const selectIsSubmitting = (state: RootState): boolean => state.medPearlOneFeedback.submittingFeedback;

export default medPearlOneFeedbackSlice.reducer;
